/**
 * Communication Emojis — public landing (v2 gallery + build-your-pack).
 *
 * Browseable grid of AAC-style emojis. Anyone can view. Logged-in users
 * pick up to 6 emojis, name their pack, and download a ZIP containing
 * platform-specific variants (Slack, Discord, Teams, Twitch, Zoom,
 * Telegram, WhatsApp stickers, etc.). Per-platform upload instructions
 * are separate downloadable PDFs listed on this page.
 *
 * Free users watch a 6-second display ad before each build (AdSense
 * web has no Rewarded format — we use a display unit in a modal with
 * a forced wait, same pattern as the single-emoji download flow).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { Search, Download, Lock, Sparkles, X as XIcon, Smile, Loader2, Check, Package, AlertTriangle, FileText, Plus, ListChecks, Users } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { useAuth } from '@/context/AuthContext';
import SubmitEmojiDialog from '@/components/emojis/SubmitEmojiDialog';
import MySubmissionsPanel from '@/components/emojis/MySubmissionsPanel';

const REWARD_WAIT_SECONDS = 6;
const API = process.env.REACT_APP_BACKEND_URL;
const MAX_PICKS = 6;

const CommunicationEmojisPage = () => {
  const { user, isAuthenticated } = useAuth();
  const [emojis, setEmojis] = useState([]);
  const [categories, setCategories] = useState([]);
  const [platformSpecs, setPlatformSpecs] = useState([]);
  const [instructionPlatforms, setInstructionPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  // Community filter — when true, only emojis that came in through the
  // public submission flow are shown. Mutually exclusive with the
  // regular category chips so the chip-row stays unambiguous.
  const [communityOnly, setCommunityOnly] = useState(false);
  const [communityTotal, setCommunityTotal] = useState(0);
  const [picked, setPicked] = useState([]);  // array of emoji_id
  const [buildOpen, setBuildOpen] = useState(false);
  const [howOpen, setHowOpen] = useState(false);  // "How it works" expandable
  const [packName, setPackName] = useState('');
  const [adCountdown, setAdCountdown] = useState(null);
  const [downloading, setDownloading] = useState(false);
  // Community submission UI state — only shown to signed-in users.
  const [submitOpen, setSubmitOpen] = useState(false);
  const [mySubmissionsOpen, setMySubmissionsOpen] = useState(false);
  const [submissionsRefreshKey, setSubmissionsRefreshKey] = useState(0);

  const isVip = user?.subscription_status === 'active' || user?.is_admin;

  // Aggregate "platforms NOT in this build" across the user's picked
  // emojis. A platform is "missing from at least one emoji" if any
  // picked emoji excludes it. Drives the warning panel in the build
  // dialog so users see what they're skipping before downloading.
  const missingPlatformsByEmoji = useMemo(() => {
    const out = [];  // [{label, missing: [platform_key, ...]}]
    for (const id of picked) {
      const e = emojis.find((x) => x.emoji_id === id);
      if (e && (e.excluded_platforms || []).length > 0) {
        out.push({ label: e.label, missing: e.excluded_platforms });
      }
    }
    return out;
  }, [picked, emojis]);

  // Initial load: gallery + categories + platform specs in parallel.
  // Specs power the "supported platforms" key in the hero and the
  // per-tile exclusion badge tooltips.
  useEffect(() => {
    (async () => {
      try {
        const [gal, cats, specs, instr] = await Promise.all([
          apiFetch('/api/emojis/gallery?limit=1000'),
          apiFetch('/api/emojis/categories'),
          apiFetch('/api/emojis/platform-specs'),
          apiFetch('/api/emojis/instructions'),
        ]);
        setEmojis(gal.data?.emojis || []);
        setCategories(cats.data?.categories || []);
        setPlatformSpecs(specs.data?.platforms || []);
        setInstructionPlatforms(instr.data?.platforms || []);
        setCommunityTotal(gal.data?.community_total || 0);
      } catch (e) {
        toast.error('Could not load the emoji gallery — please refresh.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Quick lookup: platform key → human label, used by the exclusion
  // tooltip on each tile + the build-dialog summary.
  const platformLabels = useMemo(() => {
    const out = {};
    platformSpecs.forEach((p) => { out[p.key] = p.label; });
    return out;
  }, [platformSpecs]);

  // Client-side filter: category + search + community. The backend
  // supports all three but for fast chip-switching we filter the
  // loaded list locally — usually ≤500 emojis, well within
  // "no perf concerns" territory.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return emojis.filter((e) => {
      if (communityOnly && !e.is_community) return false;
      const matchCat = activeCategory === 'all' || (e.categories || []).includes(activeCategory);
      const matchTerm = !term || (e.label || '').toLowerCase().includes(term);
      return matchCat && matchTerm;
    });
  }, [emojis, activeCategory, search, communityOnly]);

  const togglePick = useCallback((id) => {
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_PICKS) {
        toast.info(`You can pick up to ${MAX_PICKS} emojis per pack.`);
        return prev;
      }
      return [...prev, id];
    });
  }, []);

  // Build-pack ad-gate countdown. Mirrors the per-emoji flow: when the
  // counter hits 0 we POST __pack__ to the ad-viewed endpoint, then
  // unlock the Download button.
  useEffect(() => {
    if (adCountdown === null) return;
    if (adCountdown <= 0) {
      apiFetch('/api/emojis/__pack__/ad-viewed', { method: 'POST', body: {} }).then(({ error }) => {
        if (error) {
          toast.error(error);
        } else {
          toast.success('Ad complete — press Build & download.');
        }
        // Clear the countdown either way so the placeholder panel
        // disappears (it stays at "0s…" otherwise — cosmetic only).
        setAdCountdown(null);
      });
      return;
    }
    const t = setTimeout(() => setAdCountdown(adCountdown - 1), 1000);
    return () => clearTimeout(t);
  }, [adCountdown]);

  const handleBuild = useCallback(async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to download.');
      return;
    }
    if (picked.length === 0) {
      toast.error('Pick at least one emoji.');
      return;
    }
    setDownloading(true);
    try {
      // Fetch the ZIP directly — apiFetch returns JSON only, so we use
      // raw fetch here. Auth token from localStorage matches every
      // other call site (apiFetch lives at /app/frontend/src/hooks/useFetch.js).
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/emojis/build-pack`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          emoji_ids: picked,
          pack_name: packName.trim() || 'my-emoji-pack',
        }),
      });
      if (res.status === 402) {
        // Free user without unlock — open the ad countdown.
        setAdCountdown(REWARD_WAIT_SECONDS);
        toast.info('Free download requires a short ad — starting now.');
        setDownloading(false);
        return;
      }
      if (!res.ok) {
        let err = {};
        try { err = await res.json(); } catch { /* not JSON */ }
        // Pydantic 422 returns a list under `detail` — pull the human
        // bit out instead of dumping `[object Object]` into the toast.
        let msg = err?.detail;
        if (Array.isArray(msg)) {
          msg = msg.map((d) => d?.msg).filter(Boolean).join('; ') || null;
        }
        if (!msg) msg = `Build failed (HTTP ${res.status})`;
        // Friendly mapping for the common shapes the backend returns.
        if (msg === 'ad_required') {
          msg = 'Free download requires a short ad — watch one to unlock the pack.';
        } else if (/None of the chosen emojis/i.test(msg)) {
          msg = 'These emojis have no source image yet — pick different ones or contact support.';
        }
        toast.error(msg);
        setDownloading(false);
        return;
      }
      // Stream-to-disk via Blob URL.
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (packName.trim() || 'my-emoji-pack').replace(/[^a-z0-9-_]/gi, '-').slice(0, 60) || 'my-pack';
      a.download = `${safe}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Pack downloaded! Grab the platform PDF guides on this page for install steps.');
      setBuildOpen(false);
      setPicked([]);
      setPackName('');
    } catch (e) {
      toast.error('Network error: ' + e.message);
    } finally {
      setDownloading(false);
    }
  }, [picked, packName, isAuthenticated]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-[#00E5CC]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 pt-20 pb-28">
        {/* Hero */}
        <div className="mb-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight bg-gradient-to-r from-[#00E5CC] to-[#FF7A59] bg-clip-text text-transparent">
            Communication Emojis
          </h1>
          <p className="text-muted-foreground mt-3 text-base max-w-2xl mx-auto">
            Pick up to {MAX_PICKS} emojis, name your pack, and download a ZIP ready for
            Slack, Discord, Teams, Twitch, Zoom, Telegram, WhatsApp, and more.
          </p>
          {/* Community submission CTAs — only meaningful for signed-in users */}
          {isAuthenticated && (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSubmitOpen(true)}
                className="border-[#00A896]/50 text-[#00A896] hover:bg-[#00E5CC]/10"
                data-testid="emoji-submit-cta"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Submit your emoji
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setMySubmissionsOpen(true)}
                className="text-muted-foreground hover:text-[#00A896]"
                data-testid="emoji-my-submissions-cta"
              >
                <ListChecks className="h-4 w-4 mr-1.5" /> My submissions
              </Button>
            </div>
          )}
        </div>

        {/* Supported platforms + how-it-works panel */}
        {platformSpecs.length > 0 && (
          <Card className="mb-6 border-[#00A896]/30 bg-gradient-to-br from-[#00E5CC]/5 to-transparent" data-testid="emoji-howto-card">
            <CardContent className="py-4 space-y-3">
              {/* "How it works" — collapsible to keep the hero compact */}
              <button
                onClick={() => setHowOpen((v) => !v)}
                className="flex items-center gap-2 text-sm font-semibold text-[#00A896] hover:text-[#008b7a] transition"
                data-testid="emoji-howto-toggle"
              >
                <FileText className="h-4 w-4" />
                How custom packs work {howOpen ? '−' : '+'}
              </button>
              {howOpen && (
                <ol className="text-xs sm:text-sm space-y-1.5 list-decimal pl-5 text-muted-foreground" data-testid="emoji-howto-steps">
                  <li><b>Browse</b> the gallery. Tap an emoji to add it to your pack — up to {MAX_PICKS} per pack.</li>
                  <li><b>Filter</b> by category chip (Reactions, Mood, etc.) or search by label.</li>
                  <li><b>Build</b> your pack from the sticky bottom bar. Name it whatever you like — that becomes the ZIP filename.</li>
                  <li><b>Download</b> the ZIP. Free users see one short ad per download; <Link to="/subscription" className="text-[#00A896] underline">VIP</Link> skips the ad gate.</li>
                  <li><b>Install</b> on each platform using the PDF guides below — one quick download per platform.</li>
                </ol>
              )}

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  Platforms &amp; sizes included by default
                </p>
                <div className="flex flex-wrap gap-1.5" data-testid="supported-platforms-list">
                  {platformSpecs.map((p) => (
                    <Badge
                      key={p.key}
                      variant="outline"
                      className={`text-[10px] py-0.5 font-medium ${
                        p.kind === 'sticker'
                          ? 'border-purple-500/40 text-purple-600 dark:text-purple-300 bg-purple-500/5'
                          : 'border-[#00A896]/40 text-[#00A896] bg-[#00E5CC]/5'
                      }`}
                    >
                      {p.label} · {p.size[0]}×{p.size[1]}
                    </Badge>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 flex items-start gap-1.5">
                  <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-amber-500" />
                  <span>
                    Each emoji may exclude platforms where the glyph doesn&apos;t render well at that size —
                    look for the <span className="text-amber-600 dark:text-amber-400 font-medium">amber badge</span> on a tile to see which.
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Per-platform upload instructions — one downloadable PDF each.
            Instructions no longer ship inside the pack ZIP. */}
        {instructionPlatforms.length > 0 && (
          <Card className="mb-6" data-testid="emoji-instructions-section">
            <CardContent className="py-4">
              <p className="text-sm font-semibold flex items-center gap-2 mb-1">
                <FileText className="h-4 w-4 text-[#00A896]" />
                How to upload to each platform
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                Quick step-by-step PDF guide for every platform — download just the ones you need.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {instructionPlatforms.map((p) => (
                  <a
                    key={p.key}
                    href={`${API}${p.pdf_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-md border border-[#00A896]/30 bg-[#00E5CC]/5 hover:bg-[#00E5CC]/15 transition px-3 py-2 text-xs font-medium text-foreground"
                    data-testid={`instruction-pdf-${p.key}`}
                  >
                    <Download className="h-3.5 w-3.5 shrink-0 text-[#00A896]" />
                    <span className="truncate">{p.label}</span>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search + category chips */}
        <div className="sticky top-16 z-20 bg-background/95 backdrop-blur py-3 border-b mb-6">
          <div className="relative max-w-md mx-auto mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search emojis…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="emoji-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-2 justify-center" data-testid="category-chips">
            <button
              onClick={() => { setActiveCategory('all'); setCommunityOnly(false); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                activeCategory === 'all' && !communityOnly
                  ? 'bg-[#00E5CC] text-[#0d1f2d]'
                  : 'bg-muted hover:bg-muted/80 text-foreground'
              }`}
              data-testid="category-chip-all"
            >
              All ({emojis.length})
            </button>
            {communityTotal > 0 && (
              <button
                onClick={() => { setCommunityOnly((v) => !v); setActiveCategory('all'); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1.5 ${
                  communityOnly
                    ? 'bg-gradient-to-r from-[#FF7A59] to-[#FFB05A] text-white shadow-md shadow-[#FF7A59]/30'
                    : 'bg-muted hover:bg-muted/80 text-foreground border border-[#FF7A59]/30'
                }`}
                data-testid="category-chip-community"
                title="Emojis submitted by the community"
              >
                <Users className="h-3 w-3" />
                Made by community ({communityTotal})
              </button>
            )}
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => { setActiveCategory(c.slug); setCommunityOnly(false); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  activeCategory === c.slug && !communityOnly
                    ? 'bg-[#00E5CC] text-[#0d1f2d]'
                    : 'bg-muted hover:bg-muted/80 text-foreground'
                }`}
                data-testid={`category-chip-${c.slug}`}
              >
                {c.label} ({c.count})
              </button>
            ))}
          </div>
        </div>

        {/* Gallery grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <Smile className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p>No emojis match your filter.</p>
          </div>
        ) : (
          <TooltipProvider delayDuration={200}>
          <div
            className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 gap-y-7 sm:gap-y-5"
            data-testid="emoji-gallery"
          >
            {filtered.map((e) => {
              const isPicked = picked.includes(e.emoji_id);
              const excludes = e.excluded_platforms || [];
              return (
                <div key={e.emoji_id} className="flex flex-col items-center">
                  <button
                    onClick={() => togglePick(e.emoji_id)}
                    className={`group relative aspect-square w-full rounded-lg border-2 p-2 transition-all hover:scale-105 ${
                      isPicked
                        ? 'border-[#00E5CC] bg-[#00E5CC]/10 shadow-lg shadow-[#00E5CC]/20'
                        : 'border-transparent bg-muted/30 hover:border-muted-foreground/30'
                    }`}
                    data-testid={`emoji-tile-${e.emoji_id}`}
                    title={e.label}
                  >
                    <img
                      src={e.image_path?.startsWith('http') ? e.image_path : `${API}${e.image_path}`}
                      alt={e.label}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      onError={(ev) => {
                        // Some legacy emojis have an image_path the
                        // server can't resolve (file was lost during a
                        // redeploy, path slug changed, etc.). Instead
                        // of the browser's broken-image icon, swap in a
                        // text fallback that at least communicates the
                        // emoji's label. The graceful failure means the
                        // gallery still feels intentional, not broken.
                        const img = ev.currentTarget;
                        img.style.display = 'none';
                        const fb = img.nextElementSibling;
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    <div
                      className="hidden absolute inset-0 m-0 items-center justify-center text-center text-[10px] font-medium text-muted-foreground bg-amber-500/10 border border-amber-500/30 rounded p-1"
                      data-testid={`emoji-fallback-${e.emoji_id}`}
                      aria-hidden="true"
                    >
                      <span className="line-clamp-3 leading-tight">
                        {e.label || 'image missing'}
                      </span>
                    </div>
                    {isPicked && (
                      <div className="absolute top-1 right-1 bg-[#00E5CC] rounded-full p-0.5">
                        <Check className="h-3 w-3 text-[#0d1f2d]" />
                      </div>
                    )}
                    {/* Community credit chip — visible flair so submitters
                        feel rewarded and other users see real contribution. */}
                    {e.is_community && (
                      <div
                        className="absolute top-1 left-1 bg-gradient-to-r from-[#FF7A59] to-[#FFB05A] text-white rounded-full p-0.5 shadow-sm"
                        data-testid={`community-badge-${e.emoji_id}`}
                        title={e.submitted_by_name ? `Submitted by ${e.submitted_by_name}` : 'Submitted by the community'}
                      >
                        <Users className="h-3 w-3" />
                      </div>
                    )}
                    {/* Per-emoji exclusion badge — hover/long-press to see WHICH platforms */}
                    {excludes.length > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className="absolute bottom-1 left-1 bg-amber-500 text-white rounded-full px-1.5 py-0.5 text-[9px] font-bold leading-tight cursor-help flex items-center gap-0.5"
                            data-testid={`emoji-exclusion-badge-${e.emoji_id}`}
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            <AlertTriangle className="h-2 w-2" />
                            {excludes.length}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[220px] text-xs">
                          <p className="font-semibold mb-1">Not included for:</p>
                          <ul className="space-y-0.5">
                            {excludes.map((k) => (
                              <li key={k}>• {platformLabels[k] || k}</li>
                            ))}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </button>
                  {/* Always-on label below tile — works on touch devices
                      where hover tooltips never fire. Sub-text on sm+
                      stays hidden behind the tile's title attribute. */}
                  <span className="block sm:hidden mt-1 text-[10px] text-center text-muted-foreground truncate max-w-full">
                    {e.label}
                  </span>
                  {/* Always-on community credit caption — shown on every
                      breakpoint so submitters get visible credit. */}
                  {e.is_community && e.submitted_by_name && (
                    <span
                      className="block mt-0.5 text-[9px] text-center text-[#FF7A59] truncate max-w-full"
                      data-testid={`community-credit-${e.emoji_id}`}
                    >
                      by {e.submitted_by_name}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          </TooltipProvider>
        )}
      </main>

      {/* Sticky bottom bar — only shows when at least 1 emoji is picked */}
      {picked.length > 0 && (
        <div
          className="fixed bottom-0 inset-x-0 z-30 bg-background/95 backdrop-blur border-t border-[#00E5CC]/30 shadow-2xl"
          data-testid="build-pack-bar"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
            <Badge className="bg-[#00E5CC] text-[#0d1f2d] text-sm">
              {picked.length} / {MAX_PICKS} selected
            </Badge>
            <div className="flex-1 flex gap-1 overflow-x-auto py-1">
              {picked.map((id) => {
                const e = emojis.find((x) => x.emoji_id === id);
                if (!e) return null;
                return (
                  <div key={id} className="relative shrink-0">
                    <img
                      src={e.image_path?.startsWith('http') ? e.image_path : `${API}${e.image_path}`}
                      alt={e.label}
                      className="w-8 h-8 rounded border bg-muted/30"
                    />
                    <button
                      onClick={() => togglePick(id)}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                      data-testid={`pick-remove-${id}`}
                    >
                      <XIcon className="h-2 w-2" />
                    </button>
                  </div>
                );
              })}
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPicked([])}
              data-testid="clear-picks-btn"
            >
              Clear
            </Button>
            <Button
              onClick={() => setBuildOpen(true)}
              className="bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00d4bd]"
              data-testid="open-build-dialog-btn"
            >
              <Package className="h-4 w-4 mr-1" /> Build pack
            </Button>
          </div>
        </div>
      )}

      {/* Build-pack dialog */}
      <Dialog open={buildOpen} onOpenChange={setBuildOpen}>
        <DialogContent className="max-w-md" data-testid="build-pack-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#00A896]" /> Build your pack
            </DialogTitle>
            <DialogDescription>
              {picked.length} emoji{picked.length === 1 ? '' : 's'} ready to package for every major platform.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="pack-name">Pack name (optional)</Label>
              <Input
                id="pack-name"
                placeholder="my-team-reactions"
                value={packName}
                onChange={(e) => setPackName(e.target.value.slice(0, 60))}
                data-testid="pack-name-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Becomes the ZIP filename.
              </p>
            </div>

            <div className="bg-muted/30 rounded p-3 text-xs text-muted-foreground">
              <p className="font-medium text-foreground mb-1">What&apos;s in the ZIP:</p>
              <ul className="space-y-0.5">
                <li>• Slack · Discord · Teams · Zoom · Google Chat (128px emoji)</li>
                <li>• Twitch (28 / 56 / 112 — all three sizes)</li>
                <li>• Telegram · WhatsApp · Signal · iMessage (sticker formats)</li>
                <li>• Discord stickers (320×320)</li>
                <li>• Original high-res source files</li>
              </ul>
              <p className="mt-2 text-[11px]">
                Upload guides for each platform are on this page as PDF downloads.
              </p>
            </div>

            {missingPlatformsByEmoji.length > 0 && (
              <div
                className="text-xs bg-amber-500/10 border border-amber-500/30 rounded p-3 space-y-1"
                data-testid="build-exclusions-summary"
              >
                <p className="font-medium text-amber-700 dark:text-amber-300 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> Heads up — some emojis skip platforms:
                </p>
                <ul className="space-y-0.5 text-amber-700/90 dark:text-amber-300/90">
                  {missingPlatformsByEmoji.map((row) => (
                    <li key={row.label}>
                      <span className="font-medium">{row.label}</span> not included for:{' '}
                      <span className="italic">
                        {row.missing.map((k) => platformLabels[k] || k).join(', ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!isAuthenticated && (
              <div className="text-sm text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-3 flex items-center gap-2">
                <Lock className="h-4 w-4 shrink-0" />
                <span>
                  <Link to="/login" className="underline font-medium">Sign in</Link> to download your pack.
                </span>
              </div>
            )}

            {!isVip && isAuthenticated && adCountdown === null && (
              <p className="text-xs text-muted-foreground">
                Free users see a 6-second ad each download. <Link to="/subscription" className="text-[#00A896] underline">Go ad-free with VIP →</Link>
              </p>
            )}

            {adCountdown !== null && adCountdown > 0 && (
              <div className="text-center py-4 border-2 border-[#00E5CC]/30 bg-[#00E5CC]/5 rounded">
                <p className="text-sm font-medium">Sponsored ad placeholder</p>
                <p className="text-xs text-muted-foreground mt-1">Your pack will be ready in {adCountdown}s…</p>
                <Loader2 className="h-5 w-5 mx-auto mt-2 animate-spin text-[#00A896]" />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setBuildOpen(false)}
              data-testid="build-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBuild}
              disabled={downloading || !isAuthenticated || (adCountdown !== null && adCountdown > 0)}
              className="bg-[#00A896] text-white hover:bg-[#008b7a]"
              data-testid="build-download-btn"
            >
              {downloading
                ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Building…</>
                : <><Download className="h-4 w-4 mr-1" /> Build &amp; download</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Community submission flow — signed-in only */}
      <SubmitEmojiDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSubmitted={() => {
          // Bump the panel's refreshKey so it reloads if it's already open
          setSubmissionsRefreshKey((k) => k + 1);
        }}
      />
      <MySubmissionsPanel
        open={mySubmissionsOpen}
        onClose={() => setMySubmissionsOpen(false)}
        refreshKey={submissionsRefreshKey}
      />
    </div>
  );
};

export default CommunicationEmojisPage;
